import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';

const GridBackground = () => {
  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#f8fafc',
        backgroundImage: `linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)`,
        backgroundSize: '48px 48px',
        opacity: 0.4,
      }}
    />
  );
};

const FeatureBadge = ({ icon, text, delay, frame, fps }: { icon: string; text: string; delay: number; frame: number; fps: number }) => {
  const entryProgress = spring({ frame: frame - delay, fps, config: { damping: 12, mass: 0.8 } });
  const scale = interpolate(entryProgress, [0, 1], [0, 1]);
  const opacity = interpolate(entryProgress, [0, 1], [0, 1]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 18px',
        backgroundColor: 'white',
        borderRadius: 100,
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        transform: `scale(${scale})`,
        opacity,
      }}
    >
      <span style={{ fontSize: 18 }}>{icon}</span>
      <span style={{ fontSize: 15, fontWeight: 500, color: '#0f172a' }}>{text}</span>
    </div>
  );
};

const SocialPreview = ({ platform, delay, frame, fps }: { platform: 'twitter' | 'youtube' | 'producthunt'; delay: number; frame: number; fps: number }) => {
  const entryProgress = spring({ frame: frame - delay, fps, config: { damping: 12 } });
  const scale = interpolate(entryProgress, [0, 1], [0.8, 1]);
  const opacity = interpolate(entryProgress, [0, 1], [0, 1]);
  const rotation = interpolate(entryProgress, [0, 1], [5, 0]);

  const platformConfig = {
    twitter: { bg: '#1d9bf0', icon: '𝕏', label: 'Twitter/X' },
    youtube: { bg: '#ff0000', icon: '▶', label: 'YouTube' },
    producthunt: { bg: '#ff6154', icon: '🚀', label: 'Product Hunt' },
  };

  const config = platformConfig[platform];

  return (
    <div
      style={{
        width: 180,
        padding: 16,
        backgroundColor: 'white',
        borderRadius: 16,
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
        transform: `scale(${scale}) rotate(${rotation}deg)`,
        opacity,
      }}
    >
      <div
        style={{
          width: '100%',
          height: 90,
          borderRadius: 10,
          backgroundColor: config.bg,
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 28,
          color: 'white',
        }}
      >
        {config.icon}
      </div>
      <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>{config.label}</p>
      <p style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', margin: 0, marginTop: 4 }}>Ready to share</p>
    </div>
  );
};

export const IndieProductDemo = () => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // Animation phases
  const logoEntry = spring({ frame, fps, config: { damping: 12 } });
  const titleEntry = spring({ frame: frame - 15, fps, config: { damping: 15 } });
  const subtitleEntry = spring({ frame: frame - 30, fps, config: { damping: 15 } });

  // Floating animations
  const float = Math.sin(frame * 0.04) * 6;

  // Typing effect for the tagline
  const tagline = "Ship your product video today";
  const typedLength = Math.floor(interpolate(frame, [100, 160], [0, tagline.length], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }));
  const typedText = tagline.slice(0, typedLength);
  const showCursor = frame > 100 && frame % 20 < 10;

  return (
    <AbsoluteFill style={{ fontFamily: 'system-ui, sans-serif', backgroundColor: '#f8fafc', overflow: 'hidden' }}>
      <GridBackground />

      {/* Main Content */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -50%) translateY(${float}px)`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 24,
          width: width * 0.7,
        }}
      >
        {/* Product Logo */}
        <div
          style={{
            width: 100,
            height: 100,
            borderRadius: 24,
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: `scale(${interpolate(logoEntry, [0, 1], [0, 1])})`,
            boxShadow: '0 8px 30px rgba(139, 92, 246, 0.3)',
          }}
        >
          <span style={{ fontSize: 48, color: 'white' }}>⚡</span>
        </div>

        {/* Product Name */}
        <h1
          style={{
            fontSize: 56,
            fontWeight: 800,
            color: '#0f172a',
            margin: 0,
            opacity: interpolate(titleEntry, [0, 1], [0, 1]),
            transform: `translateY(${interpolate(titleEntry, [0, 1], [20, 0])}px)`,
          }}
        >
          ShipFast
        </h1>

        {/* Tagline */}
        <p
          style={{
            fontSize: 22,
            color: '#64748b',
            margin: 0,
            opacity: interpolate(subtitleEntry, [0, 1], [0, 1]),
          }}
        >
          Launch your SaaS in days, not months
        </p>

        {/* Feature badges */}
        <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
          <FeatureBadge icon="🚀" text="Fast Setup" delay={60} frame={frame} fps={fps} />
          <FeatureBadge icon="💳" text="Payments Ready" delay={75} frame={frame} fps={fps} />
          <FeatureBadge icon="🔐" text="Auth Included" delay={90} frame={frame} fps={fps} />
          <FeatureBadge icon="📧" text="Email Built-in" delay={105} frame={frame} fps={fps} />
        </div>

        {/* Typing tagline */}
        <div
          style={{
            marginTop: 32,
            padding: '16px 32px',
            backgroundColor: '#0f172a',
            borderRadius: 12,
            opacity: frame > 100 ? 1 : 0,
          }}
        >
          <span style={{ fontSize: 20, color: 'white', fontFamily: 'monospace' }}>
            {typedText}
            {showCursor && <span style={{ color: '#22c55e' }}>|</span>}
          </span>
        </div>
      </div>

      {/* Social Previews */}
      <div
        style={{
          position: 'absolute',
          bottom: 60,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 24,
        }}
      >
        <SocialPreview platform="twitter" delay={180} frame={frame} fps={fps} />
        <SocialPreview platform="youtube" delay={200} frame={frame} fps={fps} />
        <SocialPreview platform="producthunt" delay={220} frame={frame} fps={fps} />
      </div>

      {/* "NEW FEATURE" badge */}
      <div
        style={{
          position: 'absolute',
          top: 50,
          right: 80,
          transform: `rotate(12deg) scale(${interpolate(spring({ frame: frame - 40, fps }), [0, 1], [0, 1])})`,
        }}
      >
        <div style={{ padding: '8px 20px', backgroundColor: '#fef08a', borderRadius: 8, border: '2px solid #ca8a04' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#854d0e' }}>NEW FEATURE 🎉</span>
        </div>
      </div>

      {/* Decorative elements */}
      <div
        style={{
          position: 'absolute',
          top: height * 0.15,
          left: width * 0.1,
          width: 200,
          height: 200,
          borderRadius: '50%',
          backgroundColor: '#8b5cf6',
          opacity: 0.1,
          filter: 'blur(60px)',
          transform: `translateY(${Math.sin(frame * 0.03) * 15}px)`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: height * 0.3,
          right: width * 0.08,
          width: 180,
          height: 180,
          borderRadius: '50%',
          backgroundColor: '#f59e0b',
          opacity: 0.1,
          filter: 'blur(50px)',
          transform: `translateY(${Math.cos(frame * 0.035) * 15}px)`,
        }}
      />
    </AbsoluteFill>
  );
};
